"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/auth-context";
import {
  addToWishlist,
  removeFromWishlist,
  isHotelInWishlist,
} from "@/services/customer-wishlist.service";
import { LoginPromptModal } from "./LoginPromptModal";

interface WishlistButtonProps {
  hotelUid: number;
  className?: string;
  iconClassName?: string;
  showText?: boolean;
  onToggle?: (isWishlisted: boolean) => void;
}

export const WishlistButton = ({
  hotelUid,
  className = "",
  iconClassName = "w-5 h-5",
  showText = false,
  onToggle,
}: WishlistButtonProps) => {
  const auth = useAuth();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string>("/");

  // Get token from localStorage (your auth pattern)
  const getToken = () => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("authToken");
    }
    return null;
  };

  // Get current URL for redirect
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.pathname + window.location.search);
    }
  }, []);

  // Debug: Log auth state
  useEffect(() => {
    // console.log("🔍 WishlistButton Auth State:", {
    //   isAuthenticated: auth.isAuthenticated,
    //   hasToken: !!getToken(),
    //   authObject: auth,
    // });
  }, [auth]);

  const checkWishlistStatus = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setIsChecking(false);
      return;
    }

    try {
      setIsChecking(true);
      const inWishlist = await isHotelInWishlist(hotelUid, token);
      setIsWishlisted(inWishlist);
    } catch (error) {
    } finally {
      setIsChecking(false);
    }
  }, [hotelUid]);

  // Check if hotel is in wishlist on mount
  useEffect(() => {
    const token = getToken();
    // console.log(
    //   "🔍 WishlistButton useEffect - Token exists:",
    //   !!token,
    //   "isAuthenticated:",
    //   auth.isAuthenticated,
    // );

    if (token) {
      checkWishlistStatus();
    } else {
      setIsChecking(false);
    }
  }, [auth.isAuthenticated, checkWishlistStatus]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const token = getToken();

    // console.log("❤️ Wishlist toggle clicked:", {
    //   hotelUid,
    //   hasToken: !!token,
    //   isAuthenticated: auth.isAuthenticated,
    // });

    // Check authentication - just check for token
    if (!token) {
      // console.log("❌ No token - showing login modal");
      setShowLoginModal(true);
      return;
    }

    if (isLoading) return;

    try {
      setIsLoading(true);
      console.log(
        `🔄 ${isWishlisted ? "Removing from" : "Adding to"} wishlist...`,
      );

      const getErrorMessage = (data: any): string => {
        if (!data) return "Operation failed. Please try again.";
        if (typeof data === "string") return data;
        if (data.detail) return data.detail;
        if (data.title) return data.title;
        return "Operation failed. Please try again.";
      };

      if (isWishlisted) {
        // Remove from wishlist
        const response = await removeFromWishlist(hotelUid, token);

        if (response.success) {
          console.log("✅ Removed from wishlist");
          setIsWishlisted(false);
          onToggle?.(false);
        } else {
          const errorMsg = getErrorMessage(response.data);
          console.error("❌ Wishlist removal error:", errorMsg);
          throw new Error(errorMsg);
        }
      } else {
        // Add to wishlist
        const response = await addToWishlist(hotelUid, token);

        if (response.success) {
          console.log("✅ Added to wishlist");
          setIsWishlisted(true);
          onToggle?.(true);
        } else {
          const errorMsg = getErrorMessage(response.data);
          console.error("❌ Wishlist add error:", errorMsg);
          throw new Error(errorMsg);
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      console.error("❌ Error toggling wishlist:", errorMessage);
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isChecking) {
    return (
      <button
        disabled
        className={`flex items-center justify-center ${className}`}
        aria-label="Loading wishlist status"
      >
        <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
      </button>
    );
  }

  const token = getToken();
  const shouldShowButton = !!token; // Show button if token exists

  return (
    <>
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`group/btn transition-all ${className}`}
        aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
        title={
          !shouldShowButton
            ? "Sign in to save hotels"
            : isWishlisted
              ? "Remove from wishlist"
              : "Add to wishlist"
        }
      >
        {isLoading ? (
          <div
            className={`border-2 border-current border-t-transparent rounded-full animate-spin ${iconClassName}`}
          />
        ) : (
          <>
            <svg
              className={`transition-transform group-hover/btn:scale-110 ${iconClassName}`}
              fill={isWishlisted ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={isWishlisted ? 0 : 2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {showText && (
              <span className="ml-2">{isWishlisted ? "Saved" : "Save"}</span>
            )}
          </>
        )}
      </button>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        redirectUrl={currentUrl}
      />
    </>
  );
};
